<template>
    <asli-form @submitted="retrieveQuote">
        <asli-section-header class="my-[15px]" heading-level="h2">{{
            isQuoteRecallPage ? "Retrieve a Quote" : "Marketing Preferences"
        }}</asli-section-header>
        <p class="text-sm mb-[15px]">Enter the details below to retrieve a quote you have previously saved.</p>
        <asli-text-input
            id="quote_ref_recall"
            name="quote_ref"
            rules="required|quote-reference"
            :mask="QUOTE_REF_MASK"
            label="Quote Reference"
            v-model="recallDetails.reference"
            :tooltipText="QUOTE_RECALL.quote_reference"
            :forced-error-state="quoteRecallErrors.reference"
            :has-label-error-state="quoteRecallErrors.reference"
            large
        />
        <asli-text-input
            id="surname_recall"
            name="surname_recall"
            rules="required:last_name"
            label="Last Name"
            v-model="recallDetails.surname"
            autocomplete="family-name"
            :mask="NAME_MASK_ANPOST"
            :is-capitalized="true"
            :forced-error-state="quoteRecallErrors.name"
            :has-label-error-state="quoteRecallErrors.name"
            large
        />
        <asli-date
            id="date_of_birth_recall"
            name="date_of_birth_recall"
            rules="required"
            label="Date of Birth"
            initial-mode="year"
            :date-from="oldestAge"
            :date-to="youngestAge"
            :show-today="false"
            v-model="recallDetails.dob"
            autocomplete="on"
            :forced-error-state="quoteRecallErrors.dob"
            :has-label-error-state="quoteRecallErrors.dob"
            calendar-aria-label="Open calendar to select date of birth"
        />
        <recall-error />
        <button-bar>
            <asli-button id="form-continue" color="primary" type="submit" icon="download">Retrieve Quote</asli-button>
        </button-bar>
    </asli-form>
</template>

<script lang="ts" setup>
import {
    AsliForm,
    AsliTextInput,
    AsliDate,
    AsliButton,
    AsliSectionHeader,
    getDateMinusYears,
    NAME_MASK_ANPOST
} from "@mbs-npm/anpost-ui";
import ButtonBar from "@/components/ButtonBar.vue";
import { reactive, computed, onMounted } from "vue";
import { useProposalStore } from "@/stores/risk/risk";
import { GA4Helper } from "@/utils/ga4-dataLayers";
import { QUOTE_RECALL } from "@/utils/helpText";
import RecallError from "@/components/RecallError.vue";
import { useMainStore } from "@/stores/main";
import { RecallQuoteDetails } from "@/stores/types";
import { useRouter } from "vue-router";
import { QUOTE_REF_MASK } from "@/static/masks";

const mainStore = useMainStore();
const proposalStore = useProposalStore();
const router = useRouter();
const isQuoteRecallPage = computed(() => router.currentRoute.value.name === "QuoteRecall");

const recallDetails = reactive<RecallQuoteDetails>({
    reference: null,
    surname: null,
    dob: null,
    allow_expired: null
});

async function retrieveQuote() {
    await proposalStore.recallQuote({
        ...recallDetails,
        allow_expired: !isQuoteRecallPage.value
    });

    // Trigger GA4 DataLayer push
    if (recallDetails.reference) {
        GA4Helper.trackRetrieveQuote(recallDetails.reference, proposalStore.proposal.recalled_from_offline);
    }
}

const oldestAge = computed(() => getDateMinusYears(100));
const youngestAge = computed(() => getDateMinusYears(18));

function getQueryParams() {
    const params = new URLSearchParams(window.location.search);
    return {
        reference: params.get("reference"),
        surname: params.get("surname"),
        dob: params.get("dob")
    };
}

const quoteRecallErrors = computed(() => mainStore.quoteRecallErrors);

onMounted(() => {
    const queryParams = getQueryParams();
    if (queryParams) {
        if (queryParams.reference !== null) {
            recallDetails.reference = queryParams.reference;
        }
        if (queryParams.surname !== null) {
            recallDetails.surname = queryParams.surname;
        }
        if (queryParams.dob !== null) {
            recallDetails.dob = queryParams.dob;
        }
    }
});
</script>
